import * as React from 'react';
import * as toastr from 'toastr';
import * as GoogleAnalytics from 'react-ga';

import Button from '../../../components/buttons/Button';
import BillingForm from './BillingForm';
import Fieldset from '../../../components/forms/Fieldset';
import { Spinner } from '../../../components/common';
import { IconCheck, IconChevronLeft } from '../../../components/icons';
import Utils, { InputError, Validation, SecurityRoles } from '../../../common/utils';

interface Props {
	actions?: any;
	customerPolicy: CustomerPolicy;
	hidden: boolean;
	paymentDetails?: any;
	stripe?: any;
	user: UserDetails;
	onUserChange?: (e: any) => void;
	currentUser: UserDetails;
	customerDetails: CustomerDemographicDetails;
	referrals: CustomerReferralResponse;
	history?: any;
	match?: any;
}
interface State {
	card_number: boolean;
	card_cvv: boolean;
	card_expiry: boolean;
	isLoading: boolean;
}
class BillingPage extends React.Component<Props, State> {
	errors = { mailingPostalCode: {} as InputError, billingPostalCode: {} as InputError };

	constructor(props: Props) {
		super(props);
		this.state = {
			card_number: false,
			card_cvv: false,
			card_expiry: false,
			isLoading: false
		};
	}

	componentDidMount() {
		// window.onpopstate = () => null;
	}

	save = () => {
		const user = { ...this.props.user };
		if (user.isSameAddress) {
			user.billingAddress = user.mailingAddress;
			user.billingCity = user.mailingCity;
			user.billingProvince = user.mailingProvince;
			user.billingPostalCode = user.mailingPostalCode;
			user.billingCountry = user.mailingCountry;
		}

		const p1 = this.props.actions.saveUser(user);
		const p2 = this.props.actions.saveCustomerPolicy(this.props.customerPolicy);
		return Promise.all([p1, p2]);
	}
	back = () => {
		this.save()
			.then(() =>
				this.props.history.push(`/quote/${this.props.match.params.workflow}/enrollment/review`)
			);
	}
	enroll = () => {
		this.setState({ isLoading: true });

		this.save()
			.then(() => {
				const card = {
					name: this.props.paymentDetails.ccCardholderName,
					address_line1: this.props.user.billingAddress,
					address_city: this.props.user.billingCity,
					address_state: this.props.user.billingProvince,
					address_zip: this.props.user.billingPostalCode,
					// optional address_country (two character country code)
				};
				return this.props.stripe.createToken(card);
			})
			.then((result: any) => {
				if (result.error) {
					throw (result.error.message);
				}
				else {
					return result.token;
				}
			})
			.then((token: any) => {
				const resetPasswordUrl = this.props.actions.getResetPasswordUrlForEmail();
				return this.props.actions.createNewCustomerSubscription(
					this.props.user.id,
					this.props.customerPolicy.id,
					token.id,
					this.props.referrals.referralCode,
					false,
					resetPasswordUrl
				);
			})
			.then(() => this.props.actions.approveAllBeneficiariesForCustomer({ id: this.props.user.id, policyId: this.props.customerPolicy.id }))
			.then(() => this.sendAnalyticsData())
			.then(() => this.gotoSignup())
			.catch((errorMessage: string) => {
				toastr.error(errorMessage);
				this.setState({ isLoading: false });
			});
	}
	sendAnalyticsData = () => {
		const user = {
			userId: this.props.user.id,
			term: this.props.customerPolicy.term,
			rate: this.props.customerPolicy.rate,
			coverageAmount: this.props.customerPolicy.coverageAmount,
			age: this.props.customerDetails.age,
			gender: this.props.user.gender,
			smoker: this.props.customerDetails.isSmoker
		};
		GoogleAnalytics.event({
			category: 'User',
			action: 'signup',
			label: JSON.stringify(user)
		});
	}
	gotoSignup = () => {
		this.props.history.push(`/quote/${this.props.match.params.workflow}/enrollment/sign-up`);
	}

	isNextDisabled = () => {
		if (this.state.isLoading) {
			return true;
		}

		if (this.errors.mailingPostalCode.hasError ||
			(!this.props.user!.isSameAddress && this.errors.billingPostalCode.hasError)) {
			return true;
		}

		return (
			!this.state.card_number ||
			!this.state.card_cvv ||
			!this.state.card_expiry ||
			!this.props.paymentDetails.ccCardholderName ||
			!this.props.user!.mailingAddress ||
			!this.props.user!.mailingCity ||
			!this.props.user!.mailingProvince ||
			!this.props.user!.mailingPostalCode ||
			!this.props.user!.mailingCountry ||
			(!this.props.user!.isSameAddress &&
				(!this.props.user!.billingAddress ||
					!this.props.user!.billingCity ||
					!this.props.user!.billingProvince ||
					!this.props.user!.billingPostalCode ||
					!this.props.user!.billingCountry)) ||
			!this.props.user!.phoneNumber
		);
	}
	validate = () => {
		// true means invalid
		return {
			mailingPostalCode: Validation
				.validatePostalCode(this.props.user.mailingPostalCode, 'Mailing postal code is not valid.'),
			billingPostalCode: Validation
				.validatePostalCode(this.props.user.billingPostalCode, 'Billing postal code is not valid.')
		};
	}

	onStripeElementChange = (element: any, name: any) => {
		if (!element.empty && element.complete) {
			this.setState({ [name]: true });
		}
		else {
			this.setState({ [name]: false });
		}
	}
	onPhoneKeyUp = (e: any) => {
		let phone = e.target.value;
		phone = phone
			.match(/\d*/g)
			.join('')
			.match(/(\d{0,3})(\d{0,3})(\d{0,4})/)
			.slice(1)
			.join('-')
			.replace(/-*$/g, '');

		const user = {
			...this.props.user,
			[e.target.name]: phone
		};

		this.props.actions.setUser(user);
	}

	onPolicyChange = (e: any) => {
		const value = Utils.parseValueFromEvent(e);
		const customerPolicy = {
			...this.props.customerPolicy,
			[e.target.name]: value
		};

		this.props.actions.setCustomerPolicy(customerPolicy);
	}

	isCustomer = (roles: string[] | undefined) => {
		if (!roles) {
			return false;
		}
		else {
			return roles.indexOf(SecurityRoles.Customer) > -1 && roles.length === 1;
		}
	}

	render() {
		this.errors = this.validate();
		return (
			<div className="content-set" hidden={this.props.hidden}>
				<Fieldset>
					<ul className="list-unstyled d-lg-flex">
						<li className="mb-3 mb-lg-0 mr-lg-5 strong text-secondary">
							<span>Coverage</span>
							<div className="xlarge">{Utils.formatCurrency(this.props.customerPolicy!.coverageAmount, 0)} CAD</div>
						</li>
						<li className="mb-3 mb-lg-0 mr-lg-5 strong text-secondary">
							<span>Term</span>
							<div className="xlarge">{this.props.customerPolicy!.term} years</div>
						</li>
						<li>
							<span>Monthly payment</span>
							<div className="xlarge">
								{Utils.formatCurrency(this.props.customerPolicy!.rate, 2)}/month
							</div>
							<em className="text-primary font-weight-bold">First two months free</em>
						</li>
					</ul>
					<BillingForm
						actions={this.props.actions}
						paymentDetails={this.props.paymentDetails}
						user={this.props.user!}
						customerPolicy={this.props.customerPolicy}
						onUserChange={this.props.onUserChange!}
						onPolicyChange={this.onPolicyChange}
						onStripeElementChange={this.onStripeElementChange}
						onPhoneKeyUp={this.onPhoneKeyUp}
						errors={this.errors}
						isCustomer={this.isCustomer(this.props.currentUser.role)}
					/>
					<p className="small my-0">
						<em>
							By clicking Enroll below, you agree that all statements you have made throughout this application
							are true.
						</em>
					</p>
				</Fieldset>
				<div className="workflow-progress-actions">
					<Button onClick={this.back} className="btn-outline-primary workflow-progress-action">
						<IconChevronLeft />
						<span className="align-middle mx-2 d-none d-sm-inline-block">Back</span>
					</Button>
					<Button onClick={this.enroll} disabled={this.isNextDisabled()} className="btn-primary workflow-progress-action">
						<span className="align-middle mx-2">Enroll</span>
						{!this.state.isLoading && <IconCheck />}
						{this.state.isLoading && <Spinner />}
					</Button>
				</div>
			</div>
		);
	}
}

export default BillingPage;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/quoting/enrollment/billing-review/BillingPage.tsx